import type { MemberRole } from "../types.js";

export const PLAN_IDS = ["solo", "studio", "agency", "enterprise"] as const;
export type PlanId = (typeof PLAN_IDS)[number];

export interface Plan {
  id: PlanId;
  name: string;
  /** USD monthly. `null` = "Talk to us" / custom. */
  priceMonthly: number | null;
  /** USD annual. Annual ≈ 10 months — operator's call. */
  priceAnnual: number | null;
  /**
   * Stripe Price IDs for self-serve subscriptions (one per cadence).
   * Absent on Enterprise (custom / contact-sales — no self-serve price).
   * These are the SANDBOX (test-mode) ids; live-mode deploys override them
   * via env (see `resolveStripePriceId` in the API). Price ids are not
   * secret — they're safe to commit and to expose client-side.
   */
  stripePriceIdMonthly?: string;
  stripePriceIdAnnual?: string;
  cadence: string;
  blurb: string;
  /** Drafts/month included before per-draft pricing kicks in. `null` = unlimited. */
  draftsPerMonth: number | null;
  /** Max brand brains. `null` = unlimited. */
  brands: number | null;
  /** Max workspaces under one billing account. `null` = unlimited. */
  workspaces: number | null;
  /** Max seats. `null` = unlimited. */
  seats: number | null;
  /** Roles that can be invited at this tier (excludes owner, who always exists). */
  rolesAllowed: MemberRole[];
  /**
   * Included photo-generation units per month (flat workspace pool). `null`
   * means "computed per-seat" — only Enterprise, which uses
   * `mediaPhotoUnitsPerSeat` × effective seats instead (never unlimited).
   */
  mediaPhotoUnits: number | null;
  /** Included video-generation units per month (flat pool). `null` = per-seat. */
  mediaVideoUnits: number | null;
  /** Per-seat included photo units (Enterprise only; null on flat-pool plans). */
  mediaPhotoUnitsPerSeat?: number;
  /** Per-seat included video units (Enterprise only; null on flat-pool plans). */
  mediaVideoUnitsPerSeat?: number;
  /** Default billing mode for media overage at this tier. */
  mediaBillingMode: MediaBillingMode;
  /** Feature bullets to show on the pricing card. */
  features: string[];
  cta: string;
  recommended?: boolean;
}

/**
 * How a workspace handles media generation past its included pool.
 *  - "hard-cap": generation is refused once the pool is exhausted (no spend).
 *  - "packs":    draws down a prepaid unit balance; refused when that's empty.
 *  - "no-cap":   never blocked — overage accrues to a running tab (and, for
 *                Enterprise, auto-buys blocks) billed at month-end. REQUIRES a
 *                payment method on file (enforced server-side, fail-closed).
 */
export type MediaBillingMode = "hard-cap" | "packs" | "no-cap";

/**
 * Pricing plans. Single source of truth — both the marketing pricing card
 * and the billing/limits enforcement read from this. Edit here and both
 * surfaces update on next deploy.
 *
 * Sourced from docs/MONETIZATION.md tier table.
 */
export const PLANS: Record<PlanId, Plan> = {
  solo: {
    id: "solo",
    name: "Solo",
    priceMonthly: 29,
    priceAnnual: 329,
    stripePriceIdMonthly: "price_1TbwekIzoaD1mjvTifSvQ75t",
    stripePriceIdAnnual: "price_1TdOo7IzoaD1mjvTEgRVm13v",
    cadence: "per month",
    blurb: "For a single brand, learning the workflow.",
    draftsPerMonth: 150,
    brands: 1,
    workspaces: 1,
    seats: 1,
    rolesAllowed: ["owner"],
    mediaPhotoUnits: 60,
    mediaVideoUnits: 40,
    mediaBillingMode: "hard-cap",
    features: [
      "1 brand brain",
      "150 drafts / month",
      "60 photo + 40 video generation units / month",
      "All four brand kinds",
      "All three platforms (IG, FB, TikTok)",
      "Web + mobile PWA approval",
      "Shopify or WooCommerce sync",
      "Per-product photo gallery",
      "5 GB media storage",
      "Email support",
    ],
    cta: "Get started",
  },
  studio: {
    id: "studio",
    name: "Studio",
    priceMonthly: 79,
    priceAnnual: 899,
    stripePriceIdMonthly: "price_1TbwfWIzoaD1mjvTKTcLe8UD",
    stripePriceIdAnnual: "price_1TdOqAIzoaD1mjvT0n0xZZHW",
    cadence: "per month",
    blurb: "For a brand that posts every day, with a small team.",
    draftsPerMonth: 750,
    brands: 5,
    workspaces: 1,
    seats: 5,
    rolesAllowed: ["owner", "admin", "editor", "approver", "viewer"],
    mediaPhotoUnits: 160,
    mediaVideoUnits: 100,
    mediaBillingMode: "hard-cap",
    features: [
      "Up to 5 brand brains",
      "750 drafts / month",
      "160 photo + 100 video generation units / month",
      "5 seats (any role)",
      "Slack approval channel",
      "All product sources (Shopify, Woo, manual)",
      "Calendar + auto-scheduling",
      "50 GB media storage",
      "Email support · 24h SLA",
    ],
    cta: "Get started",
    recommended: true,
  },
  agency: {
    id: "agency",
    name: "Agency",
    priceMonthly: 249,
    priceAnnual: 2839,
    stripePriceIdMonthly: "price_1Tbwg9IzoaD1mjvTDGhoYybD",
    stripePriceIdAnnual: "price_1TdOt6IzoaD1mjvTTPlV2TI6",
    cadence: "per month",
    blurb: "For agencies running social for multiple clients.",
    draftsPerMonth: 3000,
    brands: null,
    workspaces: 5,
    seats: 15,
    rolesAllowed: ["owner", "admin", "editor", "approver", "viewer"],
    mediaPhotoUnits: 500,
    mediaVideoUnits: 300,
    mediaBillingMode: "hard-cap",
    features: [
      "Up to 5 workspaces (1 per client)",
      "Unlimited brand brains",
      "3,000 drafts / month, pooled",
      "500 photo + 300 video generation units / month, pooled",
      "15 seats",
      "Per-workspace Slack channels",
      "Multi-workspace calendar overlay",
      "White-label option (+$50/mo)",
      "200 GB media storage",
      "Priority support · 4h SLA",
    ],
    cta: "Get started",
  },
  enterprise: {
    id: "enterprise",
    name: "Enterprise",
    priceMonthly: null,
    priceAnnual: null,
    cadence: "custom",
    blurb: "For brands or agencies that need SSO, dedicated infra, or a contract.",
    draftsPerMonth: null,
    brands: null,
    workspaces: null,
    seats: null,
    rolesAllowed: ["owner", "admin", "editor", "approver", "viewer"],
    // Media is per-seat and never unlimited/free: pool = effective seats ×
    // per-seat allowance (min 30 seats, +5 blocks), then a no-cap running tab
    // that auto-buys blocks. See effectiveMediaAllowance() in ./media.
    mediaPhotoUnits: null,
    mediaVideoUnits: null,
    mediaPhotoUnitsPerSeat: 60,
    mediaVideoUnitsPerSeat: 40,
    mediaBillingMode: "no-cap",
    features: [
      "Everything in Agency",
      "Unlimited workspaces + brands + seats",
      "60 photo + 40 video units / seat / month, then auto-scaling usage",
      "SAML / OIDC SSO",
      "Dedicated AWS sub-account option",
      "Custom platform integrations",
      "EU data residency",
      "Annual contract · named CSM · 1h SLA",
    ],
    cta: "Talk to sales",
  },
};

export const ADDONS = [
  { id: "extra-drafts", label: "Extra drafts", price: 20, unit: "per 100 drafts" },
  { id: "extra-brand", label: "Extra brand (Studio)", price: 10, unit: "per month" },
  { id: "extra-workspace", label: "Extra workspace (Agency)", price: 30, unit: "per month" },
  { id: "ai-image-gen", label: "AI image generation", price: 0.05, unit: "per image" },
  { id: "media-overage", label: "Media generation overage", price: 0.15, unit: "per unit" },
  { id: "media-pack", label: "Media unit pack", price: 25, unit: "per 200 units" },
  { id: "media-block-enterprise", label: "Media auto-buy block (Enterprise)", price: 120, unit: "per 1,000 units" },
  { id: "extra-storage", label: "Extra storage", price: 0.05, unit: "per GB / month" },
  { id: "phone-support", label: "Phone support (Agency+)", price: 99, unit: "per month" },
] as const;

export const TRIAL_DAYS = 14;

// ── Media-generation billing constants ──────────────────────────────────────
// Single source of truth for the media unit economy. Tunable here; the worker,
// API, and Stripe settlement all read these — no magic numbers elsewhere.

/** Retail price per media unit for metered overage (no-cap running tab). USD. */
export const MEDIA_OVERAGE_PRICE_PER_UNIT = 0.15;
/** Prepaid pack: units granted + price. Available to any plan. */
export const MEDIA_PACK_UNITS = 200;
export const MEDIA_PACK_PRICE = 25;
/** Enterprise auto-buy block (volume rate): units + price added to the tab. */
export const MEDIA_ENTERPRISE_BLOCK_UNITS = 1000;
export const MEDIA_ENTERPRISE_BLOCK_PRICE = 120;
/** Enterprise media allowance floor + seat purchase granularity. */
export const ENTERPRISE_MIN_SEATS = 30;
export const ENTERPRISE_SEAT_BLOCK = 5;
